// shared/src/schemas/qa-answer.ts
// Zod schema for `brief_qa_answers` table (DDL: 0009_qa_substrate.sql:51-68
// + 0050_qa_trajectory_perspective.sql trajectory_answer/reasoning). 18 cols.
// Adds the missing Zod guard called out by audit-r3 S-002.
//
// Mirror of the QaAnswer TypeScript interface in shared/src/types/qa.ts:27-44
// — note that the interface uses `citations: ReadonlyArray<string>` (parsed
// from citations_json) while the DDL row holds the raw JSON string. This
// schema covers the RAW D1 row shape (citations_json string), not the
// parsed-into-array consumer shape.

import { z } from 'zod';
import { coerceConfidenceBucket } from '../resolvers/llm-enum-coerce.js';

// 2026-05-29 — coerced read-back: the writer now coerces before storing, but
// any legacy row with a non-canonical confidence (or a future writer path)
// is translated rather than failing the read. Unknown still rejects.
export const QaConfidenceEnum = z.preprocess(coerceConfidenceBucket, z.enum(['HIGH', 'MED', 'LOW', 'UNKNOWN']));

export const QaAnswerSchema = z.object({
  id:                    z.string(),
  brief_id:              z.string(),
  question_id:           z.string(),
  created_at:            z.string().datetime(),
  consensus_answer:      z.string(),
  consensus_reasoning:   z.string(),
  contrarian_answer:     z.string(),
  contrarian_reasoning:  z.string(),
  trajectory_answer:     z.string().nullable(),   // §9b (0050) — 4th perspective; null on legacy/pre-1.4.0 rows
  trajectory_reasoning:  z.string().nullable(),
  predicted_answer:      z.string(),
  predicted_reasoning:   z.string(),
  predicted_confidence:  QaConfidenceEnum,
  citations_json:        z.string(),               // raw JSON-encoded array
  // Q&A Calibration (0060, 2026-06-16) — structured scoreable claim the q-qa
  // 1.6.0 writer emits in /predicted, as a raw JSON string. NULL on legacy /
  // pre-1.6.0 rows and on any answer where Flash omitted the optional claim
  // (the answer still persists; it is simply not scored). The scorer parses
  // this through QaScoreableClaimSchema.
  scoreable_claim_json:  z.string().nullable(),
  tokens_in:             z.number().int().nullable(),
  tokens_out:            z.number().int().nullable(),
  cost_usd:              z.number().nullable(),
  llm_call_id:           z.string().nullable(),
});

export type QaAnswerRow = z.infer<typeof QaAnswerSchema>;
